/**
 * Which "add to home screen" story applies to the device in front of us.
 *
 * Every platform spells this differently and only Chromium exposes an API for
 * it, so guidance has to be resolved per device or it is worse than useless ,
 * telling an Android guest to tap a Share icon that isn't there reads as the
 * product being broken.
 *
 * Resolution order is capability first, platform second. `beforeinstallprompt`
 * firing is proof that a real native install prompt is available, and that
 * beats any user-agent guess. Only when no API exists do we fall back to
 * naming a menu, and then the label has to match what the guest actually sees.
 */

export type InstallPlatform =
  /** Already running from the home screen / dock , nothing to offer. */
  | { kind: 'installed' }
  /** Chromium fired beforeinstallprompt: one tap, real system dialog. */
  | { kind: 'native' }
  /** iOS/iPadOS Safari , Share is in the toolbar. */
  | { kind: 'ios-safari' }
  /** Chrome/Edge/Firefox on iOS: same WebKit limit, different Share menu. */
  | { kind: 'ios-other'; browser: string }
  | { kind: 'android-firefox' }
  | { kind: 'samsung-internet' }
  /** Other Android browsers with no install API , generic menu wording. */
  | { kind: 'android-other' }
  /** macOS Safari 17+ , "Add to Dock" rather than home screen. */
  | { kind: 'desktop-safari' }
  /** Desktop Chromium before beforeinstallprompt has fired, or anything else. */
  | { kind: 'unsupported' };

/** True when the page is already running as an installed app. `standalone` is
 *  the iOS-only signal; the display-mode query covers Chromium and macOS. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  if (iosStandalone === true) return true;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches === true
  );
}

export function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ identifies as a Mac; touch points are what separate them.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * @param hasNativePrompt whether a `beforeinstallprompt` event has been captured.
 */
export function detectInstallPlatform(hasNativePrompt: boolean): InstallPlatform {
  if (typeof window === 'undefined') return { kind: 'unsupported' };
  if (isStandalone()) return { kind: 'installed' };
  if (hasNativePrompt) return { kind: 'native' };

  const ua = navigator.userAgent;

  if (isIos()) {
    /* On iOS every engine is WebKit, but the Share menu that holds "Add to
       Home Screen" is drawn by the browser, so the wording has to follow the
       browser rather than the OS. Order matters: Chrome's iOS UA contains
       "CriOS", Edge's "EdgiOS", Firefox's "FxiOS", and all three also contain
       "Safari". */
    if (/CriOS/.test(ua)) return { kind: 'ios-other', browser: 'Chrome' };
    if (/EdgiOS/.test(ua)) return { kind: 'ios-other', browser: 'Edge' };
    if (/FxiOS/.test(ua)) return { kind: 'ios-other', browser: 'Firefox' };
    return { kind: 'ios-safari' };
  }

  if (/Android/.test(ua)) {
    if (/SamsungBrowser/.test(ua)) return { kind: 'samsung-internet' };
    if (/Firefox|FxiOS/.test(ua)) return { kind: 'android-firefox' };
    return { kind: 'android-other' };
  }

  // Desktop Safari gained "Add to Dock" in macOS Sonoma. Chromium desktop is
  // excluded here because it would have fired beforeinstallprompt above.
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua);
  if (isSafari) return { kind: 'desktop-safari' };

  return { kind: 'unsupported' };
}

/** Whether this device can receive web push *as it currently stands* , i.e.
 *  without being installed first. Drives whether the modal is a nice-to-have
 *  or the only route to notifications. */
export function pushNeedsInstall(): boolean {
  if (typeof window === 'undefined') return false;
  return isIos() && !('PushManager' in window);
}
